import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Mole } from "./Mole";
import { CtaButton } from "./CtaButton";
import { EASE } from "./Reveal";

const LINES = ["Ваш кротик", "присмотрит", "за интернетом"];

export default function Hero({ onCta }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const moleY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative overflow-hidden pb-16 pt-28 sm:pt-36 lg:pb-24"
    >
      <div
        aria-hidden="true"
        className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-nora-pink/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -right-24 top-64 h-80 w-80 rounded-full bg-nora-mint/10 blur-3xl"
      />
      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <h1
            data-testid="hero-title"
            className="font-display text-4xl font-black leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span
                key={line}
                className="block overflow-hidden pb-[0.14em] -mb-[0.14em]"
              >
                <motion.span
                  className={`block ${i === 2 ? "text-nora-accent" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.9,
                    delay: 0.15 + i * 0.13,
                    ease: EASE,
                  }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            data-testid="hero-subtitle"
            className="mt-6 max-w-md text-base leading-relaxed text-nora-ink/70 md:text-lg"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
          >
            Смотрите и слушайте то, что обычно недоступно.
          </motion.p>
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
          >
            <CtaButton onClick={onCta} testId="hero-cta-button" />
            <p
              data-testid="hero-note"
              className="mt-4 text-sm text-nora-ink/55"
            >
              Без сложных настроек — одна кнопка, и все работает.
            </p>
          </motion.div>
        </div>

        <motion.div
          style={{ y: moleY }}
          className="relative mx-auto w-full max-w-xs sm:max-w-sm lg:max-w-md"
        >
          <div
            aria-hidden="true"
            className="absolute inset-x-2 bottom-0 top-14 rounded-t-[999px] border border-nora-ink/5 bg-nora-surface shadow-inner"
          />
          <motion.div
            className="relative"
            initial={{ y: 170, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 55,
              damping: 14,
              delay: 0.45,
            }}
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <Mole
                data-testid="hero-mascot"
                className="relative mx-auto w-60 drop-shadow-xl sm:w-72 lg:w-full"
              />
            </motion.div>
          </motion.div>
          <motion.div
            aria-hidden="true"
            className="absolute right-2 top-8 h-4 w-4 rounded-full bg-nora-pink/50"
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            aria-hidden="true"
            className="absolute left-0 top-24 h-3 w-3 rounded-full bg-nora-mint/50"
            animate={{ y: [0, 10, 0] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </motion.div>
      </div>
    </section>
  );
}
